// ═══════════════════════════════════════════════════════
// CHANGE PASSWORD SCREEN
// Tela de primeiro acesso — força o cliente a definir
// uma senha pessoal antes de acessar o portal.
// ═══════════════════════════════════════════════════════
import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { supabase, invokeConfirmPasswordChange } from "../supabaseClient.js";

export default function ChangePasswordScreen({ onDone, T: _T }) {
  const [form, setForm] = useState({ pass: "", confirm: "" });
  const [show, setShow] = useState(false); // toggle visibilidade
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false); // sucesso — aguardando redirect automático
  const [err, setErr] = useState(null);
  const timerRef = useRef(null);

  // Cleanup do setTimeout ao desmontar
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const strength = (p) => {
    if (!p) return { label: "", pct: 0, color: "transparent" };
    if (p.length < 8) return { label: "Muito curta", pct: 20, color: "#DC2626" };
    const hasNum = /[0-9]/.test(p);
    const hasUp = /[A-Z]/.test(p);
    const hasSym = /[^A-Za-z0-9]/.test(p);
    const score = [p.length >= 12, hasNum, hasUp, hasSym].filter(Boolean).length;
    if (score <= 1) return { label: "Fraca", pct: 40, color: "#D97706" };
    if (score === 2) return { label: "Moderada", pct: 65, color: "#2563EB" };
    if (score === 3) return { label: "Boa", pct: 82, color: "#16A34A" };
    return { label: "Forte", pct: 100, color: "#16A34A" };
  };

  const s = strength(form.pass);

  const handleSave = async () => {
    if (form.pass.length < 8) {
      setErr("A senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (/^\d+$/.test(form.pass)) {
      setErr("A senha não pode ser composta apenas de números.");
      return;
    }
    if (form.pass !== form.confirm) {
      setErr("As senhas não coincidem.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      // ── Troca de senha via Edge Function (server-side, service_role) ──
      // Uso de invokeConfirmPasswordChange em vez de supabase.auth.updateUser diretamente
      // impede que o cliente remova o flag must_change_password sem realmente trocar a senha
      // (ex: DevTools → supabase.auth.updateUser({ data: { must_change_password: false } })).
      await invokeConfirmPasswordChange(form.pass);
      // ── Fix Bug P0 (Sprint 5): Edge Function usa service_role, não dispara USER_UPDATED
      // no cliente. Forçamos refreshSession() para que o cliente receba o user_metadata
      // atualizado (must_change_password=false) via evento TOKEN_REFRESHED, que já chama
      // setSession() no onAuthStateChange do App root. Isso desmonta este componente
      // automaticamente sem precisar que onDone() faça nada.
      await supabase.auth.refreshSession();
      setSaved(true);
      // onDone() como fallback caso TOKEN_REFRESHED demore > 2 s
      timerRef.current = setTimeout(() => onDone(), 2000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Tela de sucesso ───────────────────────────────────
  if (saved)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,#0B2447 0%,#19376D 100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Inter',sans-serif",
        }}
      >
        <div style={{ textAlign: "center", color: "#fff" }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
            Senha criada com sucesso!
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>
            Redirecionando para o portal...
          </div>
          <div
            style={{
              marginTop: 24,
              width: 40,
              height: 4,
              borderRadius: 4,
              background: "rgba(255,255,255,0.2)",
              overflow: "hidden",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <div
              style={{
                height: "100%",
                background: "#C9A227",
                borderRadius: 4,
                animation: "progress2s linear forwards",
                width: "100%",
              }}
            />
          </div>
        </div>
      </div>
    );

  const inp = (id, label, ph) => (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "rgba(255,255,255,0.6)",
          display: "block",
          marginBottom: 5,
        }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type={show ? "text" : "password"}
          value={form[id]}
          placeholder={ph}
          onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))}
          style={{
            width: "100%",
            padding: "11px 44px 11px 14px",
            borderRadius: 9,
            border: "1.5px solid rgba(255,255,255,0.15)",
            background: "rgba(255,255,255,0.07)",
            color: "#fff",
            fontSize: 14,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 16,
            color: "rgba(255,255,255,0.4)",
          }}
        >
          {show ? "🙈" : "👁"}
        </button>
      </div>
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#0B2447 0%,#19376D 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter',sans-serif",
        padding: 24,
      }}
    >
      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 9,
            background: "#C9A227",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 900,
            fontSize: 18,
            color: "#0B2447",
          }}
        >
          L
        </div>
        <span style={{ color: "#C9A227", fontWeight: 800, fontSize: 16, letterSpacing: 1 }}>
          LPA WEALTH
        </span>
      </div>

      {/* Card */}
      <div
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 18,
          padding: "32px 36px",
          maxWidth: 440,
          width: "100%",
          backdropFilter: "blur(10px)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔐</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginBottom: 6 }}>
            Defina sua nova senha
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
            Por segurança, você precisa criar uma senha pessoal antes de acessar o portal.
          </div>
        </div>

        {inp("pass", "Nova senha", "Mínimo 8 caracteres")}

        {/* Barra de força */}
        {form.pass && (
          <div style={{ marginTop: -8, marginBottom: 14 }}>
            <div
              style={{
                height: 4,
                borderRadius: 4,
                background: "rgba(255,255,255,0.1)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${s.pct}%`,
                  background: s.color,
                  borderRadius: 4,
                  transition: "width .3s, background .3s",
                }}
              />
            </div>
            <div style={{ fontSize: 11, color: s.color, fontWeight: 700, marginTop: 4 }}>
              {s.label}
            </div>
          </div>
        )}

        {inp("confirm", "Confirmar senha", "Repita a senha")}

        {/* Dicas */}
        <div
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 9,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 11,
            color: "rgba(255,255,255,0.45)",
            lineHeight: 1.8,
          }}
        >
          Dica: use letras maiúsculas, números e símbolos (!@#) para uma senha forte.
        </div>

        {err && (
          <div
            style={{
              background: "rgba(220,38,38,0.15)",
              border: "1px solid rgba(220,38,38,0.4)",
              borderRadius: 9,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: 13,
              color: "#FCA5A5",
              fontWeight: 600,
            }}
          >
            ⚠️ {err}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving || !form.pass || !form.confirm}
          style={{
            width: "100%",
            padding: "13px 0",
            borderRadius: 10,
            border: "none",
            background: saving || !form.pass || !form.confirm ? "rgba(201,162,39,0.4)" : "#C9A227",
            color: "#0B2447",
            fontWeight: 800,
            fontSize: 15,
            cursor: saving || !form.pass || !form.confirm ? "not-allowed" : "pointer",
            transition: ".2s",
          }}
        >
          {saving ? "⏳ Salvando…" : "Criar minha senha →"}
        </button>
      </div>

      <div style={{ marginTop: 20, fontSize: 11, color: "rgba(255,255,255,0.2)" }}>
        Seus dados são protegidos pela LGPD e armazenados no Brasil.
      </div>
    </div>
  );
}

ChangePasswordScreen.propTypes = {
  onDone: PropTypes.func.isRequired,
  T: PropTypes.object,
};
